<script setup lang="ts">
import { getLicenseDetail } from '~/server/detail'

const { locale, localePath, t } = useAppI18n()
const route = useRoute()

const normalizeTextValue = (value: unknown) => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const getFirstRouteValue = (value: unknown) => {
  if (Array.isArray(value)) return normalizeTextValue(value[0])
  return normalizeTextValue(value)
}
const routeAlias = getFirstRouteValue(route.params.slug)
const { CountryCode } = useAppStore()
type RegulationTabKey = 'crypto' | 'other'

interface RegulationItem {
  id?: string | number
  title: string
  firstLine?: string
  regulatorShortName?: string
  licenseName?: string
  subLicenseNumber?: number
  agency?: string
  regulatorFullName?: string
  status?: string
  regulationStatusIcon?: boolean
  licenseStatusColor?: string
  regulationId?: string
  licenseNumber?: string
  countryName?: string
  countryFlag?: string
  circleFlag?: string
  licensedEntity?: string
  effectiveTime?: string
  isShare?: string | number | boolean
  badgeImage?: string
  projectCode?: string
}

interface RegulationField {
  key: string
  label: string
  value: string
  flag?: string
  isCountry?: boolean
}

interface AgencyLayoutState {
  clamp: boolean
  fontSize: 14 | 13 | 12
  lineHeight: 22 | 20 | 18
}

interface LicenseTabItem {
  tabName?: string
  tabItems?: Array<{
    countryFlag?: string
    countryName?: string
    regulatorShortName?: string
    regulatorFullName?: string
    licenseName?: string
    simpleName?: string
    firstLine?: string
    licenseStatus?: string
    regulationStatusIcon?: boolean
    licenseStatusColor?: string
    regulationId?: string
    regulationNumber?: string
    holdingCompanyName?: string
    effectiveTime?: string
    effectiveDate?: string
    isShare?: string | number | boolean
    licenseType?: string
    subLicenseNumber?: number
    circleFlag?: string
    projectCode?: string
  }>
  tabDesc?: string
  isShowRemain?: boolean
  remainCount?: number
}

interface BusinessInfoTips {
  text?: string
  color?: string
}

interface BusinessInfo {
  showText?: string
  title?: string
  tips?: BusinessInfoTips
  supportDocs?: string[]
  riskWarning?: string
  supportDocCount?: number
}

type RegulationTranslateMap = Partial<
  Record<'RegulationNumber' | 'CountryName' | 'HoldingCompanyName' | 'ShowEffectiveTime' | 'ShareTraderCodes', string>
>

interface ExchangeRegulationCardProps {
  items?: LicenseTabItem[]
  abnormalLicenses?: NonNullable<LicenseTabItem['tabItems']>
  businessInfo?: BusinessInfo | null
  translateMap?: RegulationTranslateMap | null
  projectCode?: string
  emptyDataSourceText?: string
  emptyTitle?: string
  emptySubtitle?: string
  exchangeName?: string
}

const props = withDefaults(defineProps<ExchangeRegulationCardProps>(), {
  items: () => [],
  abnormalLicenses: () => [],
  businessInfo: null,
  translateMap: null,
  exchangeName: '',
})

const emit = defineEmits<{
  open: [item: RegulationItem]
}>()

const appStore = useAppStore()
const activeTab = ref<RegulationTabKey>('crypto')
const isRegulationListHovered = ref(false)
const isAbnormalDialogOpen = ref(false)
const isNoticeDialogOpen = ref(false)
const tabListRef = ref<HTMLElement | null>(null)
const tabButtonMap = new Map<RegulationTabKey, HTMLButtonElement>()
let tabScrollFrame = 0
const DEFAULT_LICENSE_STATUS_COLOR = '#1F845A'
const REGULATION_STATUS_ICON_PATHS = {
  normal:
    'M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20ZM11.003 16L6.76 11.757L8.174 10.343L11.003 13.172L16.659 7.515L18.074 8.929L11.003 16Z',
  abnormal:
    'M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C14.1217 20 16.1566 19.1571 17.6569 17.6569C19.1571 16.1566 20 14.1217 20 12C20 9.87827 19.1571 7.84344 17.6569 6.34315C16.1566 4.84285 14.1217 4 12 4C9.87827 4 7.84344 4.84285 6.34315 6.34315C4.84285 7.84344 4 9.87827 4 12C4 14.1217 4.84285 16.1566 6.34315 17.6569C7.84344 19.1571 9.87827 20 12 20ZM12 10.586L14.828 7.757L16.243 9.172L13.414 12L16.243 14.828L14.828 16.243L12 13.414L9.172 16.243L7.757 14.828L10.586 12L7.757 9.172L9.172 7.757L12 10.586Z',
} as const

const buildRegulationTitle = (item: NonNullable<LicenseTabItem['tabItems']>[number]) => {
  const regulatorShortName = item.regulatorShortName?.trim() || ''
  const licenseName = item.licenseName?.trim() || ''
  const fallbackTitle = item.simpleName?.trim() || regulatorShortName || licenseName || '--'

  if (!regulatorShortName || !licenseName) {
    return fallbackTitle
  }

  return `${regulatorShortName} ${licenseName}`
}

const normalizeIsShareValue = (value: unknown) => {
  if (value === true || normalizeTextValue(value).toLowerCase() === 'true') return t('84190')
  if (value === false || normalizeTextValue(value).toLowerCase() === 'false') return t('84191')
  return normalizeTextValue(value)
}

const normalizeApiItem = (item: NonNullable<LicenseTabItem['tabItems']>[number], index: number): RegulationItem => ({
  id: item.regulationId || index,
  title: buildRegulationTitle(item),
  firstLine: item.firstLine || '',
  regulatorShortName: item.regulatorShortName || '',
  licenseName: item.licenseName || item.simpleName || '',
  subLicenseNumber: item.subLicenseNumber,
  agency: item.regulatorFullName || item.regulatorShortName || '--',
  regulatorFullName: item.regulatorFullName || '',
  status: item.licenseStatus || '--',
  regulationStatusIcon: item.regulationStatusIcon,
  licenseStatusColor: item.licenseStatusColor || '',
  regulationId: item.regulationId || '',
  licenseNumber: item.regulationNumber || '--',
  countryName: item.countryName || '--',
  countryFlag: item.countryFlag,
  licensedEntity: item.holdingCompanyName || '--',
  effectiveTime: normalizeTextValue(item.effectiveTime || item.effectiveDate) || '--',
  isShare: normalizeIsShareValue(item.isShare ?? item.licenseType) || '--',
  circleFlag: item.circleFlag,
  projectCode: item.projectCode,
})

const regulationTabs = computed(() => {
  if (props.items.length > 0) {
    return props.items.map((tab, index) => ({
      key: (index === 0 ? 'crypto' : 'other') as RegulationTabKey,
      label: tab.tabName,
      items: (tab.tabItems || []).map((item, itemIndex) => normalizeApiItem(item, itemIndex)),
      count: tab.tabItems?.length || 0,
      tabDesc: tab.tabDesc || '',
      isShowRemain: Boolean(tab.isShowRemain),
      remainCount: tab.remainCount || 0,
    }))
  }

  return [
    {
      key: 'crypto' as const,
      label: t('83577'),
      items: [],
      count: 0,
      tabDesc: '',
      isShowRemain: false,
      remainCount: 0,
    },
    { key: 'other' as const, label: t('83578'), items: [], count: 0, tabDesc: '', isShowRemain: false, remainCount: 0 },
  ]
})

const activeTabData = computed(
  () => regulationTabs.value.find((tab) => tab.key === activeTab.value) || regulationTabs.value[0],
)
const hasMultipleTabs = computed(() => regulationTabs.value.length > 1)
const activeTabIndex = computed(() => {
  const index = regulationTabs.value.findIndex((tab) => tab.key === activeTab.value)
  return index >= 0 ? index + 1 : 0
})
const activeItems = computed(() => activeTabData.value?.items || [])
const activeItem = computed(() => activeItems.value[0] || null)
const itemCount = computed(() => activeItems.value.length)
const hasActiveRegulation = computed(() => itemCount.value > 0)
const emptyDataSourceText = computed(() => props.emptyDataSourceText || t('83579'))
const emptyTitle = computed(() => props.emptyTitle || t('83581'))
const emptySubtitle = computed(() => props.emptySubtitle || t('83582'))
const hasSingleItem = computed(() => itemCount.value === 1)
const hasTwoItems = computed(() => itemCount.value === 2)
const hasMoreThanFour = computed(() => itemCount.value > 4)
const isForeignRegulationLayout = computed(() => !['zh', 'zh-cn'].includes(String(locale || '').toLowerCase()))

const summaryItems = computed(() => {
  if (hasMoreThanFour.value && !isRegulationListHovered.value) return activeItems.value.slice(0, 4)
  return activeItems.value
})

const expandedSummaryIndex = computed(() => {
  return hasTwoItems.value ? 1 : 0
})

const expandedSummaryItem = computed(() => summaryItems.value[expandedSummaryIndex.value] || activeItem.value)

const currentTabDesc = computed(() => activeTabData.value?.tabDesc?.trim() || '')
const hasBusinessInfo = computed(() => Boolean(props.businessInfo))
const showNotice = computed(() => hasActiveRegulation.value && Boolean(currentTabDesc.value) && hasBusinessInfo.value)
const showFooter = computed(() => !showNotice.value && !(hasMoreThanFour.value && !isRegulationListHovered.value))
const noticeDialogData = computed(() => {
  const supportDocs = Array.isArray(props.businessInfo?.supportDocs)
    ? props.businessInfo.supportDocs.filter((item) => typeof item === 'string' && item.trim())
    : []
  const supportDocCount = props.businessInfo?.supportDocCount || supportDocs.length

  return {
    title: props.businessInfo?.title?.trim() || t('83584'),
    headline: props.businessInfo?.tips?.text?.trim() || props.businessInfo?.showText?.trim() || '--',
    headlineColor: props.businessInfo?.tips?.color?.trim() || '#C62828',
    description: props.businessInfo?.riskWarning?.trim() || props.businessInfo?.showText?.trim() || '--',
    proofLabel: t('83583'),
    proofText: `图片*${supportDocCount}`,
    proofIcon: 'https://resources1.interface003.com/web30/2026/06/10/23555258-9754-4b43-a402-3a37ed1d3b06.png',
    buttonText: t('83585'),
    supportDocs,
  }
})
const agencyLayoutMap = ref<Record<string, AgencyLayoutState>>({})
const agencyElementMap = new Map<string, HTMLElement>()
let agencyMeasureFrame = 0
let agencyResizeObserver: ResizeObserver | null = null
const canUseDom = typeof window !== 'undefined' && typeof document !== 'undefined'
const agencySizeOptions: AgencyLayoutState[] = [
  { clamp: false, fontSize: 14, lineHeight: 22 },
  { clamp: false, fontSize: 13, lineHeight: 20 },
  { clamp: false, fontSize: 12, lineHeight: 18 },
]
const secondLineMinWidthRatio = 0.2

const getTranslatedFieldLabel = (code: keyof RegulationTranslateMap) => {
  const label = props.translateMap?.[code]?.trim()
  return label ? `${label}：` : ''
}

const buildSingleFields = (item: RegulationItem): RegulationField[] => [
  { key: 'license-number', label: getTranslatedFieldLabel('RegulationNumber'), value: item.licenseNumber || '--' },
  {
    key: 'country',
    label: getTranslatedFieldLabel('CountryName'),
    value: item.countryName || '--',
    flag: item.countryFlag,
    isCountry: true,
  },
  { key: 'licensed-entity', label: getTranslatedFieldLabel('HoldingCompanyName'), value: item.licensedEntity || '--' },
  { key: 'effective-date', label: getTranslatedFieldLabel('ShowEffectiveTime'), value: item.effectiveTime || '--' },
  { key: 'license-type', label: getTranslatedFieldLabel('ShareTraderCodes'), value: item.isShare || '--' },
]

const buildMultiFields = (item: RegulationItem): RegulationField[] => [
  { key: 'license-number', label: getTranslatedFieldLabel('RegulationNumber'), value: item.licenseNumber || '--' },
  { key: 'licensed-entity', label: getTranslatedFieldLabel('HoldingCompanyName'), value: item.licensedEntity || '--' },
]

const regulationFields = computed(() => {
  if (!expandedSummaryItem.value) return []
  if (!hasSingleItem.value) return []
  return buildSingleFields(expandedSummaryItem.value)
})
const noticeCertificateTo = computed(() => {
  const exchangeSlug = getFirstRouteValue(route.params.slug)

  if (!exchangeSlug) return ''

  return localePath({
    path: `/exchanges/${exchangeSlug}/licenses`,
    query: {
      from: 'regulation-card',
    },
  })
})

const abnormalRegulationItems = computed(() =>
  (props.abnormalLicenses || []).map((item, index) => normalizeApiItem(item, index)),
)

const getItemKey = (item: RegulationItem, index: number, suffix = '') =>
  `${suffix}${String(item.id ?? item.title ?? 'agency')}-${index}`

const isHongKongLocalCountry = () => {
  const countryCode = (appStore.CountryCode || '').trim()

  return countryCode === '344'
}

const getLicenseStatusColor = (item: RegulationItem) => item.licenseStatusColor || DEFAULT_LICENSE_STATUS_COLOR
const getStatusAlphaColor = (item: RegulationItem, alpha: number) => {
  const color = getLicenseStatusColor(item).trim()
  const hexMatch = /^#?([0-9a-f]{6})$/i.exec(color)

  if (!hexMatch) return color

  const [, hex] = hexMatch
  const red = Number.parseInt(hex.slice(0, 2), 16)
  const green = Number.parseInt(hex.slice(2, 4), 16)
  const blue = Number.parseInt(hex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}
const getForeignRegulatorShortName = (item: RegulationItem) => item.regulatorShortName?.trim() || ''
const getForeignLicenseStatus = (item: RegulationItem) => item.status || '--'
const getForeignLicenseName = (item: RegulationItem) => item.licenseName?.trim() || ''
const getForeignLicenseLine = (item: RegulationItem) =>
  `${item.countryName?.trim() || ''} ${getForeignLicenseName(item)}`
const getRegulationStatusIconPath = (item: RegulationItem) => {
  if (item.regulationStatusIcon === false) {
    return REGULATION_STATUS_ICON_PATHS.abnormal
  }

  return REGULATION_STATUS_ICON_PATHS.normal
}

const setAgencyRef = (key: string) => (element: Element | null) => {
  const previousElement = agencyElementMap.get(key)
  if (previousElement && previousElement !== element && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }

  if (element instanceof HTMLElement) {
    agencyElementMap.set(key, element)
    agencyResizeObserver?.observe(element)
    return
  }

  if (previousElement && agencyResizeObserver) {
    agencyResizeObserver.unobserve(previousElement)
  }
  agencyElementMap.delete(key)
  delete agencyLayoutMap.value[key]
}

const measureAgencyLayout = (element: HTMLElement, layout: AgencyLayoutState) => {
  if (!canUseDom) {
    return {
      lineCount: 1,
      lastLineWidthRatio: 1,
    }
  }

  const width = element.clientWidth
  const text = element.textContent?.trim() || ''

  if (!width || !text) {
    return {
      lineCount: 1,
      lastLineWidthRatio: 1,
    }
  }

  const computedStyle = window.getComputedStyle(element)
  const clone = document.createElement('p')
  clone.textContent = text
  clone.style.position = 'fixed'
  clone.style.left = '-99999px'
  clone.style.top = '0'
  clone.style.visibility = 'hidden'
  clone.style.pointerEvents = 'none'
  clone.style.margin = '0'
  clone.style.padding = '0'
  clone.style.border = '0'
  clone.style.width = `${width}px`
  clone.style.fontFamily = computedStyle.fontFamily
  clone.style.fontWeight = computedStyle.fontWeight
  clone.style.letterSpacing = computedStyle.letterSpacing
  clone.style.fontSize = `${layout.fontSize}px`
  clone.style.lineHeight = `${layout.lineHeight}px`
  clone.style.whiteSpace = 'normal'
  clone.style.wordBreak = 'break-word'
  clone.style.overflowWrap = 'anywhere'

  document.body.appendChild(clone)

  const range = document.createRange()
  range.selectNodeContents(clone)
  const rects = Array.from(range.getClientRects()).filter((rect) => rect.width > 0)

  range.detach?.()
  clone.remove()

  const lineWidths = rects.map((rect) => rect.width)
  const lastLineWidth = lineWidths[lineWidths.length - 1] || width

  return {
    lineCount: Math.max(lineWidths.length, 1),
    lastLineWidthRatio: Math.min(lastLineWidth / width, 1),
  }
}

const measureAgencyMultiLineMap = () => {
  if (!canUseDom) return

  const nextMap: Record<string, AgencyLayoutState> = {}

  agencyElementMap.forEach((element, key) => {
    const width = element.clientWidth
    if (!width) {
      nextMap[key] = agencyLayoutMap.value[key] ?? agencySizeOptions[0]
      return
    }

    const layouts = agencySizeOptions.map((layout) => ({
      layout,
      measure: measureAgencyLayout(element, layout),
    }))
    const singleLineLayout = layouts.find(({ measure }) => measure.lineCount <= 1)

    if (singleLineLayout) {
      nextMap[key] = singleLineLayout.layout
      return
    }

    const balancedTwoLineLayout = layouts.find(
      ({ measure }) => measure.lineCount === 2 && measure.lastLineWidthRatio >= secondLineMinWidthRatio,
    )

    if (balancedTwoLineLayout) {
      nextMap[key] = balancedTwoLineLayout.layout
      return
    }

    const twoLineLayout = layouts.find(({ measure }) => measure.lineCount <= 2)

    if (twoLineLayout) {
      nextMap[key] = twoLineLayout.layout
      return
    }

    nextMap[key] = { ...agencySizeOptions[agencySizeOptions.length - 1], clamp: true }
  })

  agencyLayoutMap.value = nextMap
}

const getAgencyLayoutState = (key: string) => agencyLayoutMap.value[key] ?? agencySizeOptions[0]

const getAgencyScaleStyle = (key: string) => {
  const layout = getAgencyLayoutState(key)
  return {
    display: layout.clamp ? '-webkit-box' : 'block',
    fontSize: `${layout.fontSize}px`,
    lineHeight: `${layout.lineHeight}px`,
    overflow: layout.clamp ? 'hidden' : 'visible',
    textOverflow: layout.clamp ? 'ellipsis' : 'clip',
    WebkitBoxOrient: layout.clamp ? 'vertical' : undefined,
    WebkitLineClamp: layout.clamp ? '2' : undefined,
  }
}

const updateAgencyMultiLineMap = async () => {
  if (!canUseDom) return

  await nextTick()

  if (agencyMeasureFrame) {
    cancelAnimationFrame(agencyMeasureFrame)
    agencyMeasureFrame = 0
  }

  measureAgencyMultiLineMap()
}

const setTabButtonRef = (key: RegulationTabKey) => (element: Element | null) => {
  if (element instanceof HTMLButtonElement) {
    tabButtonMap.set(key, element)
    return
  }

  tabButtonMap.delete(key)
}

const scrollActiveTabIntoView = async (behavior: ScrollBehavior = 'smooth') => {
  if (!canUseDom) return

  await nextTick()

  if (tabScrollFrame) {
    cancelAnimationFrame(tabScrollFrame)
    tabScrollFrame = 0
  }

  tabScrollFrame = requestAnimationFrame(() => {
    const tabList = tabListRef.value
    const activeButton = tabButtonMap.get(activeTab.value)

    if (!tabList || !activeButton) return

    activeButton.scrollIntoView({
      behavior,
      block: 'nearest',
      inline: activeTab.value === 'crypto' ? 'start' : 'center',
    })
  })
}

const switchRegulationTab = (key: RegulationTabKey) => {
  if (activeTab.value === key) return
  activeTab.value = key
  matomo.trackEvent('broker_regulator_tab', {
    af_type: activeTabData.value?.label || '',
    af_type1: String(activeTabIndex.value || ''),
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
  })
  scrollActiveTabIntoView()
}

const getLicenseData = (regulationId: string) => {
  const key = `${routeAlias}_${locale}_${CountryCode}_licenseData_${regulationId}`
  const raw = window.localStorage.getItem(key)
  if (!raw) return null
  const { data, expire } = JSON.parse(raw)
  if (Date.now() > expire) {
    window.localStorage.removeItem(key)
    return null
  }
  return data
}

// 缓存存储牌照数据
const localStoreLicenseDataFunc = async (regulationId: string) => {
  let licenseDetail = null
  if (getLicenseData(regulationId)) {
    licenseDetail = getLicenseData(regulationId)
  } else {
    licenseDetail = await getLicenseDetail(props.projectCode || '', regulationId, '')
  }
  const EXPIRE = 24 * 60 * 60 * 1000
  window.localStorage.setItem(
    `${routeAlias}_${locale}_${CountryCode}_licenseData_${regulationId}`,
    JSON.stringify({ data: licenseDetail, expire: Date.now() + EXPIRE }),
  )
}

// 集合Hover操作
const handleHoverLicense = async (regulationId: string) => {
  await localStoreLicenseDataFunc(regulationId)
}

const handleRegulationListMouseEnter = () => {
  if (!hasMoreThanFour.value) return
  isRegulationListHovered.value = true
}

const handleRegulationListMouseLeave = () => {
  isRegulationListHovered.value = false
}

const openAbnormalDialog = () => {
  matomo.trackEvent('broker_regulator_tab', {
    af_type: '风险',
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
  })
  if (!abnormalRegulationItems.value.length) return
  isAbnormalDialogOpen.value = true
}

const openNoticeDialog = () => {
  isNoticeDialogOpen.value = true
}

const closeAbnormalDialog = () => {
  if (!isAbnormalDialogOpen.value) return
  isAbnormalDialogOpen.value = false
}

const closeNoticeDialog = () => {
  if (!isNoticeDialogOpen.value) return
  isNoticeDialogOpen.value = false
}

const matomo = useMatomo()
const openRegulationItem = (item: RegulationItem) => {
  matomo.trackEvent('broker_regulator_click', {
    af_type: activeTabData.value?.label || '',
    af_type1: String(activeTabIndex.value || ''),
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
    af_name1: item.regulatorFullName || '',
    af_code1: item.regulationId || '',
  })
  matomo.trackEvent('licenses_details', {
    af_name: props.exchangeName || '',
    af_code: props.projectCode || '',
  })
  emit('open', item)
}

watch(
  () => [activeTab.value, itemCount.value, props.items.length],
  () => {
    isRegulationListHovered.value = false
    isNoticeDialogOpen.value = false
  },
  { immediate: true },
)

watch(
  () => [
    activeTab.value,
    itemCount.value,
    isRegulationListHovered.value,
    isAbnormalDialogOpen.value,
    summaryItems.value.length,
    abnormalRegulationItems.value.length,
  ],
  () => {
    updateAgencyMultiLineMap()
  },
  { immediate: true },
)

onMounted(() => {
  scrollActiveTabIntoView('auto')

  if (!canUseDom || typeof ResizeObserver === 'undefined') return

  agencyResizeObserver = new ResizeObserver(() => {
    updateAgencyMultiLineMap()
  })
  agencyElementMap.forEach((element) => {
    agencyResizeObserver?.observe(element)
  })
  updateAgencyMultiLineMap()
  window.addEventListener('resize', updateAgencyMultiLineMap)
})

onBeforeUnmount(() => {
  if (!canUseDom) return

  if (tabScrollFrame) {
    cancelAnimationFrame(tabScrollFrame)
  }
  if (agencyMeasureFrame) {
    cancelAnimationFrame(agencyMeasureFrame)
  }
  agencyResizeObserver?.disconnect()
  agencyResizeObserver = null
  window.removeEventListener('resize', updateAgencyMultiLineMap)
})
</script>

<template>
  <section
    class="regulation-card-shell"
    :class="{ 'regulation-card-shell-expanded': hasMoreThanFour && isRegulationListHovered }"
  >
    <div
      class="regulation-card"
      :class="{
        'regulation-card-expanded': hasMoreThanFour && isRegulationListHovered,
        'regulation-card-no-bottom-padding': itemCount > 4 && !isRegulationListHovered,
      }"
      aria-labelledby="exchange-regulation-title"
    >
      <div class="regulation-tabs">
        <div ref="tabListRef" class="regulation-tab-list" role="tablist" aria-label="监管类型">
          <button
            v-for="tab in regulationTabs"
            :id="`exchange-regulation-tab-${tab.key}`"
            :key="tab.key"
            :ref="setTabButtonRef(tab.key)"
            class="regulation-tab"
            :class="{
              'regulation-tab-active': activeTab === tab.key,
              'regulation-tab-active-indicator': activeTab === tab.key && hasMultipleTabs,
              'regulation-tab-single': !hasMultipleTabs,
            }"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.key"
            :aria-controls="`exchange-regulation-panel-${tab.key}`"
            @click="switchRegulationTab(tab.key)"
          >
            <span v-if="tab.key === 'crypto'" id="exchange-regulation-title" class="regulation-tab-label">
              {{ tab.label }}
              <span v-if="tab.count > 0" class="regulation-tab-count">{{ tab.count }}</span>
            </span>
            <span v-else class="regulation-tab-label">
              {{ tab.label }}
              <span v-if="tab.count > 0" class="regulation-tab-count">{{ tab.count }}</span>
            </span>
          </button>
        </div>
        <div class="regulation-tab-spacer" aria-hidden="true"></div>
      </div>

      <div
        :id="`exchange-regulation-panel-${activeTab}`"
        class="regulation-panel"
        role="tabpanel"
        :aria-labelledby="`exchange-regulation-tab-${activeTab}`"
      >
        <div v-if="!activeItem" class="regulation-empty-state">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/07/02/a713a4ab-5436-4099-8dfc-c9a84e2a6a0b.png"
            class="regulation-empty-icon"
            alt=""
            loading="eager"
            object-fit="contain"
          />
          <div class="regulation-empty-copy">
            <p class="regulation-empty-title">{{ emptyTitle }}</p>
            <p class="regulation-empty-subtitle">{{ emptySubtitle }}</p>
          </div>
        </div>

        <div
          v-else
          class="regulation-detail"
          :class="{ 'regulation-detail-scrollable': hasMoreThanFour }"
          @mouseenter="handleRegulationListMouseEnter"
          @mouseleave="handleRegulationListMouseLeave"
        >
          <div
            class="regulation-summary-list regulation-summary-list-main"
            :class="{
              'regulation-summary-list-main-scroll': hasMoreThanFour,
              'regulation-summary-list-main-exact-four': itemCount === 4,
            }"
          >
            <template v-for="(item, index) in summaryItems" :key="getItemKey(item, index, 'main-')">
              <article
                class="regulation-summary"
                :class="{
                  'regulation-summary-expanded': hasTwoItems && expandedSummaryIndex === index,
                  'regulation-summary-no-divider': itemCount >= 4 && index === summaryItems.length - 1,
                }"
                @mouseenter="handleHoverLicense(item.regulationId || '')"
                @click="openRegulationItem(item)"
              >
                <div class="regulation-summary-content">
                  <CommonImage
                    :src="item.circleFlag"
                    class="regulation-summary-badge"
                    alt=""
                    loading="eager"
                    object-fit="cover"
                  />

                  <div class="regulation-summary-copy">
                    <template v-if="isForeignRegulationLayout">
                      <h3 class="regulation-summary-title regulation-summary-foreign-title">
                        <span
                          v-if="getForeignRegulatorShortName(item)"
                          class="regulation-summary-short-name"
                          :style="{
                            color: getLicenseStatusColor(item),
                            backgroundColor: getStatusAlphaColor(item, 0.06),
                            borderColor: getStatusAlphaColor(item, 0.2),
                          }"
                        >
                          {{ getForeignRegulatorShortName(item) }}
                        </span>
                        <span class="regulation-summary-license-status" :style="{ color: getLicenseStatusColor(item) }">
                          {{ getForeignLicenseStatus(item) }}
                        </span>
                      </h3>
                      <p
                        :ref="setAgencyRef(getItemKey(item, index, 'main-license-'))"
                        class="regulation-summary-license-line"
                        :style="getAgencyScaleStyle(getItemKey(item, index, 'main-license-'))"
                      >
                        {{ getForeignLicenseLine(item) }}
                      </p>
                    </template>

                    <template v-else>
                      <h3 class="regulation-summary-title" :style="{ color: getLicenseStatusColor(item) }">
                        {{ item.title }}
                        <span v-if="(item.subLicenseNumber || 0) > 0" class="regulation-summary-title-count">
                          {{ item.subLicenseNumber }}
                        </span>
                      </h3>
                      <p
                        :ref="setAgencyRef(getItemKey(item, index, 'main-'))"
                        class="regulation-summary-agency"
                        :style="getAgencyScaleStyle(getItemKey(item, index, 'main-'))"
                      >
                        {{ item.agency || '--' }}
                      </p>
                    </template>
                  </div>

                  <div class="regulation-summary-side">
                    <span
                      v-if="!isForeignRegulationLayout && isHongKongLocalCountry()"
                      class="regulation-summary-status-icon"
                      :style="{ color: getLicenseStatusColor(item) }"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path :d="getRegulationStatusIconPath(item)" fill="currentColor" />
                      </svg>
                    </span>
                    <span
                      v-else-if="!isForeignRegulationLayout"
                      class="regulation-summary-status"
                      :style="{ color: getLicenseStatusColor(item) }"
                      >{{ item.status || '--' }}</span
                    >
                    <span class="regulation-summary-arrow" aria-hidden="true"></span>
                  </div>
                </div>
              </article>

              <dl
                v-if="hasTwoItems && expandedSummaryIndex === index"
                class="regulation-detail-fields regulation-detail-fields-inline"
              >
                <div
                  v-for="field in buildMultiFields(item)"
                  :key="`${item.id || index}-${field.key}`"
                  class="regulation-field"
                >
                  <dt class="regulation-field-label">{{ field.label }}</dt>
                  <dd class="regulation-field-value">{{ field.value }}</dd>
                </div>
              </dl>
            </template>
          </div>

          <dl v-if="hasSingleItem && regulationFields.length > 0" class="regulation-detail-fields">
            <div v-for="field in regulationFields" :key="field.key" class="regulation-field">
              <dt class="regulation-field-label">{{ field.label }}</dt>
              <dd class="regulation-field-value">
                <span v-if="field.isCountry" class="regulation-field-country">
                  <CommonImage
                    v-if="field.flag"
                    :src="field.flag"
                    class="regulation-country-flag"
                    alt=""
                    loading="eager"
                    object-fit="cover"
                  />
                  <span>{{ field.value }}</span>
                </span>
                <span v-else>{{ field.value }}</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div
        v-if="showFooter"
        class="regulation-footer"
        :class="{ 'regulation-footer-expanded': hasMoreThanFour && isRegulationListHovered }"
        :aria-label="emptyDataSourceText"
      >
        <div class="box">
          <span class="regulation-footer-line" aria-hidden="true"></span>
          <span class="regulation-footer-text">{{ emptyDataSourceText }}</span>
          <span class="regulation-footer-line" aria-hidden="true"></span>
        </div>
      </div>

      <button
        v-if="showNotice"
        class="regulation-notice"
        :class="{ 'regulation-notice-expanded': hasMoreThanFour && isRegulationListHovered }"
        type="button"
        @click="openNoticeDialog"
      >
        <span class="regulation-notice-copy">
          <CommonImage
            src="https://resources1.interface003.com/web30/2026/07/14/1a26d1db-7054-468d-92cd-c2d4ef56a078.png"
            class="regulation-notice-icon"
            alt=""
            loading="eager"
            object-fit="contain"
          />
          <span class="regulation-notice-text">{{ currentTabDesc }}</span>
        </span>
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/07/14/80bda966-72b5-4b43-ae19-160f62c7333e.png"
          class="regulation-notice-arrow"
          alt=""
          loading="eager"
          object-fit="contain"
        />
      </button>

      <button
        type="button"
        class="regulation-corner-badge-button"
        aria-label="打开异常监管弹窗"
        @click="openAbnormalDialog"
        v-if="abnormalRegulationItems.length > 0"
      >
        <CommonImage
          src="https://resources1.interface003.com/web30/2026/07/07/4c4309ca-cbbd-4ea8-b5d3-41a6fca8e2a9.png"
          class="regulation-corner-badge 12"
          alt=""
          object-fit="contain"
          loading="eager"
        />
      </button>

      <Teleport to="body">
        <div v-if="isAbnormalDialogOpen" class="abnormal-regulation-dialog-mask">
          <section
            class="abnormal-regulation-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="abnormal-regulation-dialog-title"
          >
            <header class="abnormal-regulation-dialog-header">
              <h2 id="abnormal-regulation-dialog-title" class="abnormal-regulation-dialog-title">{{ t('84580') }}</h2>
              <button
                type="button"
                class="abnormal-regulation-dialog-close"
                aria-label="关闭异常监管弹窗"
                @click="closeAbnormalDialog"
              >
                <span class="abnormal-regulation-dialog-close-mark"></span>
              </button>
            </header>

            <div class="abnormal-regulation-dialog-body">
              <article
                v-for="(item, index) in abnormalRegulationItems"
                :key="getItemKey(item, index, 'abnormal-')"
                class="abnormal-regulation-item"
                :class="{ 'abnormal-regulation-item-last': index === abnormalRegulationItems.length - 1 }"
                @click="openRegulationItem(item)"
                @mouseenter="localStoreLicenseDataFunc(item.regulationId || '')"
              >
                <div class="abnormal-regulation-item-content">
                  <CommonImage
                    :src="item.circleFlag"
                    class="abnormal-regulation-item-badge"
                    alt=""
                    loading="eager"
                    object-fit="cover"
                  />
                  <div class="abnormal-regulation-item-copy">
                    <template v-if="isForeignRegulationLayout">
                      <h3 class="abnormal-regulation-item-title regulation-summary-foreign-title">
                        <span
                          v-if="getForeignRegulatorShortName(item)"
                          class="regulation-summary-short-name"
                          :style="{
                            color: getLicenseStatusColor(item),
                            backgroundColor: getStatusAlphaColor(item, 0.06),
                            borderColor: getStatusAlphaColor(item, 0.2),
                          }"
                        >
                          {{ getForeignRegulatorShortName(item) }}
                        </span>
                        <span class="regulation-summary-license-status" :style="{ color: getLicenseStatusColor(item) }">
                          {{ getForeignLicenseStatus(item) }}
                        </span>
                      </h3>
                      <p
                        :ref="setAgencyRef(getItemKey(item, index, 'abnormal-license-'))"
                        class="regulation-summary-license-line abnormal-regulation-item-license-line"
                        :style="getAgencyScaleStyle(getItemKey(item, index, 'abnormal-license-'))"
                      >
                        {{ getForeignLicenseLine(item) }}
                      </p>
                    </template>

                    <template v-else>
                      <h3 class="abnormal-regulation-item-title">{{ item.title }}</h3>
                      <p class="abnormal-regulation-item-agency">{{ item.agency || '--' }}</p>
                    </template>
                  </div>
                  <div class="abnormal-regulation-item-side">
                    <span
                      v-if="!isForeignRegulationLayout && isHongKongLocalCountry()"
                      class="regulation-summary-status-icon"
                      :style="{ color: getLicenseStatusColor(item) }"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path :d="getRegulationStatusIconPath(item)" fill="currentColor" />
                      </svg>
                    </span>
                    <span
                      v-else-if="!isForeignRegulationLayout"
                      class="regulation-summary-status"
                      :style="{ color: getLicenseStatusColor(item) }"
                      >{{ item.status || '--' }}</span
                    >

                    <span class="abnormal-regulation-item-arrow" aria-hidden="true"></span>
                  </div>
                </div>
              </article>
            </div>
          </section>
        </div>
      </Teleport>

      <Teleport to="body">
        <div v-if="isNoticeDialogOpen" class="notice-dialog-mask">
          <section class="notice-dialog" role="dialog" aria-modal="true" aria-labelledby="notice-dialog-title">
            <header class="notice-dialog-header">
              <h2 id="notice-dialog-title" class="notice-dialog-title">{{ noticeDialogData.title }}</h2>
              <button type="button" class="notice-dialog-close" aria-label="关闭交易所弹窗" @click="closeNoticeDialog">
                <span class="notice-dialog-close-mark"></span>
              </button>
            </header>

            <div class="notice-dialog-body">
              <div class="notice-dialog-content">
                <div class="notice-dialog-copy">
                  <p class="notice-dialog-headline" :style="{ color: noticeDialogData.headlineColor }">
                    {{ noticeDialogData.headline }}
                  </p>
                  <div class="notice-dialog-description-wrap">
                    <p class="notice-dialog-description">{{ noticeDialogData.description }}</p>
                  </div>
                  <div class="notice-dialog-proof">
                    <span class="notice-dialog-proof-label">{{ noticeDialogData.proofLabel }}</span>
                    <NuxtLink
                      v-if="noticeCertificateTo"
                      class="notice-dialog-proof-link"
                      :to="noticeCertificateTo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CommonImage
                        :src="noticeDialogData.proofIcon"
                        class="notice-dialog-proof-icon"
                        alt=""
                        loading="eager"
                        object-fit="contain"
                      />
                      <span>{{ noticeDialogData.proofText }}</span>
                    </NuxtLink>
                  </div>
                </div>

                <button type="button" class="notice-dialog-confirm" @click="closeNoticeDialog">
                  {{ noticeDialogData.buttonText }}
                </button>
              </div>
            </div>
          </section>
        </div>
      </Teleport>
    </div>
  </section>
</template>

<style scoped lang="less">
.regulation-card-shell {
  position: relative;
  width: 100%;
  min-height: 418px;
  overflow: visible;
}

.regulation-card-shell-expanded {
  z-index: 99;
}

.regulation-card-shell-expanded .regulation-card {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.regulation-card {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 418px;
  height: auto;
  max-height: 700px;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: #fff;
  box-shadow: inset 0 0 0 0.5px rgba(11, 18, 40, 0.14);
  padding: 4px 12px 20px;
}

.regulation-card-expanded {
  height: auto;
  overflow: hidden;
  box-shadow:
    inset 0 0 0 0.5px rgba(11, 18, 40, 0.14),
    0 0 0 8px rgba(6, 88, 99, 0.08);
}

.regulation-card-no-bottom-padding {
  padding-bottom: 0;
}

.regulation-card-expanded .regulation-panel {
  min-height: 0;
  flex: 1 1 auto;
}

.regulation-card-expanded .regulation-detail {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
}

.regulation-card-expanded .regulation-summary-list-main {
  min-height: auto;
  flex: 0 0 auto;
}

.regulation-card-expanded .regulation-summary-list-main > .regulation-summary {
  min-height: auto;
  max-height: none;
  flex: 0 0 auto;
  padding: 20px 0;
}

.regulation-card-expanded .regulation-summary-list-main-scroll > .regulation-summary {
  padding: 0 8px;
}

.regulation-tabs {
  display: flex;
  width: 100%;
  padding: 0 48px 0 8px;
  align-items: center;
  justify-content: space-between;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
}

.regulation-tab-list {
  display: flex;
  min-width: 0;
  flex: 1 1 auto;
  align-items: center;
  gap: 20px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.regulation-tab-list::-webkit-scrollbar {
  display: none;
}

.regulation-tab {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  min-height: 58px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: #7d818a;
  font-size: 18px;
  font-weight: 400;
  line-height: 26px;
  padding: 16px 0;
  white-space: nowrap;
  cursor: pointer;
}

.regulation-tab-single {
  cursor: default;
}

.regulation-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.regulation-tab-count {
  color: #7d818a;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  padding: 0 5px;
  height: 22px;
  border-radius: 100px;
  background: #f8f8f8;
}

.regulation-tab-active {
  color: #292a2e;
  font-weight: 600;
}

.regulation-tab-active-indicator::after {
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 28px;
  height: 4px;
  border-radius: 999px;
  background: #292a2e;
  content: '';
  transform: translateX(-50%);
}

.regulation-tab-spacer {
  width: 14px;
  height: 20px;
  flex: 0 0 auto;
}

.regulation-panel {
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.regulation-empty-state {
  display: flex;
  width: 100%;
  min-height: 276px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  border: 1px dashed rgba(5, 21, 36, 0.06);
  border-radius: 8px;
  background: #f8f8f8;
  margin-top: 20px;
  padding: 16px;
}

.regulation-empty-icon {
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.regulation-empty-copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
}

.regulation-empty-title,
.regulation-empty-subtitle {
  margin: 0;
}

.regulation-empty-title {
  color: #292a2e;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}

.regulation-empty-subtitle {
  color: #7d818a;
  font-size: 16px;
  line-height: 24px;
}

.regulation-detail {
  display: flex;
  width: 100%;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.regulation-detail-scrollable {
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.regulation-detail-scrollable::-webkit-scrollbar {
  display: none;
}

.regulation-summary-list {
  display: flex;
  width: 100%;
  flex-direction: column;
}

.regulation-summary-list-main {
  min-height: 0;
  flex: 0 0 auto;
}

.regulation-summary-list-main-scroll {
  flex: 1 1 auto;
}

.regulation-summary-list-main > .regulation-summary {
  height: 89px;
}

.regulation-summary-list-main-exact-four > .regulation-summary {
  height: 79px;
}

.regulation-summary-list-main-exact-four .regulation-summary-content {
  padding-top: 15px;
  padding-bottom: 15px;
}

.regulation-summary-list-main-scroll > .regulation-summary {
  flex: 1 1 0;
  border-radius: 4px;
}

.regulation-summary {
  display: flex;
  width: 100%;
  border: 0;
  background: transparent;
  font: inherit;
  padding: 0 8px;
  text-align: left;
  cursor: pointer;
}

.regulation-summary:hover {
  background: #f8f8f8;
}

.regulation-summary-content {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  align-items: center;
  gap: 12px;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
  border-radius: 4px;
  padding: 20px 0;
  overflow: hidden;
}

.regulation-summary-no-divider .regulation-summary-content {
  box-shadow: none;
}

.regulation-summary-badge {
  display: block;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  object-fit: cover;
  border-radius: 40px;
  overflow: hidden;
}

.regulation-summary-copy {
  min-width: 0;
  flex: 1 0 0;
}

.regulation-summary-title {
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.regulation-summary-title {
  color: #1f845a;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.regulation-summary-title-count {
  margin-left: 4px;
  height: 14px;
  line-height: 14px;
  border-radius: 2px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  padding: 0 3px;
  color: #7d818a;
  font-size: 12px;
}

.regulation-summary-foreign-title {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
}

.regulation-summary-short-name {
  display: inline-flex;
  max-width: 100%;
  min-width: 0;
  height: 20px;
  align-items: center;
  justify-content: center;
  border: 0.5px solid;
  border-radius: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1f845a;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  padding: 0 4px;
}

.regulation-summary-license-status {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.regulation-summary-license-line {
  display: block;
  width: 100%;
  margin: 4px 0 0;
  overflow: visible;
  color: #292a2e;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  text-overflow: clip;
  white-space: normal;
  overflow-wrap: anywhere;
}

.regulation-summary-agency {
  margin: 0;
  margin-top: 4px;
  color: #292a2e;
  font-size: 14px;
  line-height: 22px;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.regulation-summary-side {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.regulation-summary-status {
  display: inline-block;
  color: #1f845a;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.regulation-summary-status-icon {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  color: currentColor;
}

.regulation-summary-status-icon svg {
  display: block;
  width: 24px;
  height: 24px;
}

.regulation-summary-arrow,
.regulation-notice-arrow {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}

.regulation-summary-arrow::before {
  display: block;
  width: 6px;
  height: 6px;
  border-top: 1.5px solid #7d818a;
  border-right: 1.5px solid #7d818a;
  content: '';
  transform: translate(4px, 4px) rotate(45deg);
}

.regulation-summary-arrow::before {
  border-top-color: #7d818a;
  border-right-color: #7d818a;
}

.regulation-detail-fields {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
  margin: 0;
  padding: 20px 8px;
}

.regulation-field {
  display: flex;
  align-items: flex-start;
}

.regulation-field-label {
  flex-shrink: 0;
}

.regulation-field-label,
.regulation-field-value {
  margin: 0;
  color: #292a2e;
  font-size: 14px;
  line-height: 20px;
}

.regulation-field-value {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}

.regulation-field-country {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.regulation-country-flag {
  display: block;
  width: 22px;
  height: 14px;
  flex: 0 0 22px;
  object-fit: cover;
}

.regulation-notice {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  border: 0;
  border-radius: 4px;
  background: rgba(201, 55, 44, 0.06);
  padding: 8px 12px;
  cursor: pointer;
}

.regulation-notice-expanded {
  margin-top: 10px;
}

.regulation-notice-copy {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1 1 auto;
}

.regulation-notice-icon {
  display: block;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  object-fit: contain;
}

.regulation-notice-text {
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #292a2e;
  font-size: 12px;
  line-height: 18px;
}

.regulation-footer {
  display: flex;
  width: 100%;
  justify-content: center;
  .box {
    width: 262px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.regulation-footer-expanded {
  margin-top: 10px;
}

.regulation-footer-line {
  height: 0;
  flex: 1 0 0;
  border-top: 0.5px solid rgba(5, 21, 36, 0.06);
}

.regulation-footer-text {
  color: #b7b9be;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}

.regulation-corner-badge-button {
  position: absolute;
  top: 0;
  right: 0;
  width: 52px;
  height: 62px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.regulation-corner-badge-button:focus-visible {
  border-radius: 12px;
  outline: 2px solid rgba(13, 63, 99, 0.28);
  outline-offset: 2px;
}

.regulation-corner-badge {
  position: absolute;
  top: 0;
  right: 0;
  width: 52px;
  height: 62px;
  pointer-events: all;
  cursor: pointer;
}

.abnormal-regulation-dialog-body {
  display: flex;
  flex-direction: column;
  padding: 20px 24px;
}

.abnormal-regulation-item {
  display: flex;
  cursor: pointer;
  padding: 0 8px;
  border-radius: 4px;
}

.abnormal-regulation-item:hover {
  background: #f8f8f8;
}

.abnormal-regulation-item-content {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 12px;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
  padding: 20px 0;
  border-radius: 4px;
}

.abnormal-regulation-item-last .abnormal-regulation-item-content {
  box-shadow: none;
}

.abnormal-regulation-item-badge {
  display: block;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  object-fit: cover;
  border-radius: 40px;
  overflow: hidden;
}

.abnormal-regulation-item-copy {
  min-width: 0;
  flex: 1 0 0;
}

.abnormal-regulation-item-title,
.abnormal-regulation-item-agency {
  margin: 0;
}

.abnormal-regulation-item-title {
  color: #e2483d;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}

.abnormal-regulation-item-agency {
  margin-top: 4px;
  color: #292a2e;
  font-size: 14px;
  line-height: 22px;
}

.abnormal-regulation-item-license-line {
  width: 100%;
}

.abnormal-regulation-item-side {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.abnormal-regulation-item-status {
  color: #e2483d;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}

.abnormal-regulation-item-arrow {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}

.abnormal-regulation-item-arrow::before {
  display: block;
  width: 6px;
  height: 6px;
  border-top: 1.5px solid #b7b9be;
  border-right: 1.5px solid #b7b9be;
  content: '';
  transform: translate(4px, 4px) rotate(45deg);
}

.abnormal-regulation-dialog-mask,
.notice-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(11, 18, 40, 0.36);
  padding: 24px;
}

.abnormal-regulation-dialog,
.notice-dialog {
  width: 540px;
  max-width: calc(100vw - 48px);
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(8, 19, 62, 0.18);
}

.abnormal-regulation-dialog-header,
.notice-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 16px;
  box-shadow: inset 0 -0.5px 0 rgba(5, 21, 36, 0.06);
}

.abnormal-regulation-dialog-title,
.notice-dialog-title {
  margin: 0;
  color: #292a2e;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.abnormal-regulation-dialog-close,
.notice-dialog-close {
  position: relative;
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: #f3f5f8;
  cursor: pointer;
  padding: 0;
}

.abnormal-regulation-dialog-close:hover,
.notice-dialog-close:hover {
  background: #eceff4;
}

.abnormal-regulation-dialog-close-mark,
.notice-dialog-close-mark {
  position: relative;
  display: block;
  width: 12px;
  height: 12px;
}

.abnormal-regulation-dialog-close-mark::before,
.abnormal-regulation-dialog-close-mark::after,
.notice-dialog-close-mark::before,
.notice-dialog-close-mark::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 1.5px;
  border-radius: 999px;
  background: #7d818a;
  content: '';
  transform-origin: center;
}

.abnormal-regulation-dialog-close-mark::before,
.notice-dialog-close-mark::before {
  transform: translate(-50%, -50%) rotate(45deg);
}

.abnormal-regulation-dialog-close-mark::after,
.notice-dialog-close-mark::after {
  transform: translate(-50%, -50%) rotate(-45deg);
}

.notice-dialog-body {
  padding: 20px 32px 32px;
}

.notice-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

.notice-dialog-copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
}

.notice-dialog-headline {
  margin: 0;
  color: #e2483d;
  font-size: 18px;
  font-weight: 500;
  line-height: 26px;
}

.notice-dialog-description-wrap {
  border-radius: 4px;
  background: #f8f8f8;
  padding: 16px;
}

.notice-dialog-description {
  margin: 0;
  color: #292a2e;
  font-size: 14px;
  line-height: 22px;
}

.notice-dialog-proof {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 4px;
}

.notice-dialog-proof-label {
  color: #7d818a;
  font-size: 14px;
  line-height: 22px;
}

.notice-dialog-proof-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #0d3f63;
  font-size: 14px;
  line-height: 22px;
  text-decoration: none;
  cursor: pointer;
}

.notice-dialog-proof-link-disabled {
  cursor: default;
  opacity: 0.6;
}

.notice-dialog-proof-icon {
  display: block;
  width: 16px;
  height: 16px;
}

.notice-dialog-confirm {
  min-width: 184px;
  height: 46px;
  border: 0;
  border-radius: 999px;
  background: #112d73;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  cursor: pointer;
  padding: 0 20px;
}
html[dir='rtl'] {
  .regulation-card-shell {
    .regulation-summary-license-line {
      text-align: right;
    }

    .regulation-summary-arrow::before {
      transform: translate(6px, 4px) rotate(-135deg);
    }
  }
  .regulation-notice-arrow {
    transform: rotate(180deg);
  }
  .abnormal-regulation-item-arrow::before {
    transform: translate(6px, 4px) rotate(-135deg);
  }
}
</style>
